import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import { POLICIES } from "./policiesContent";

/**
 * Builds a policy view from i18n keys, used as a fallback when POLICIES.{key}
 * has no entry for the current language (fr/de/es). Reads title/intro from
 * `{policyKey}_title` and `{policyKey}_intro`, and up to 6 sections from
 * `{policyKey}_h{i}` + `{policyKey}_p{i}`.
 */
function buildFromI18n(policyKey, t) {
  const sections = [];
  for (let i = 1; i <= 8; i += 1) {
    const h = t(`${policyKey}_h${i}`);
    const p = t(`${policyKey}_p${i}`);
    if (h && h !== `${policyKey}_h${i}` && p && p !== `${policyKey}_p${i}`) {
      sections.push({ h, p });
    }
  }
  return {
    title: t(`${policyKey}_title`),
    intro: t(`${policyKey}_intro`),
    sections,
  };
}

/**
 * Renders a policy/legal page using structured content from policiesContent.js.
 * Props:
 *  - testid: base data-testid
 *  - policyKey: key in POLICIES (terms, refund, privacy, shipping, legal)
 */
export default function PolicyPage({ testid, policyKey }) {
  const { lang, t } = useLang();
  const policy = POLICIES[policyKey];
  if (!policy) return null;

  const localised = policy[lang];
  const data = localised || buildFromI18n(policyKey, t);
  const dateStr = new Date(policy.updated).toLocaleDateString(
    ({ it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" })[lang] || "it-IT",
    { day: "2-digit", month: "long", year: "numeric" },
  );

  // If the fallback lacks structured sections (shouldn't happen because we populate
  // 5-6 sections per language), gracefully degrade to Italian.
  const finalData = data.sections?.length ? data : policy.it;
  const routeMap = { refund: "/rimborsi", privacy: "/privacy", terms: "/termini", shipping: "/spedizioni", legal: "/note-legali" };
  const path = routeMap[policyKey] || "/";

  return (
    <div className="min-h-screen bg-xd-light" data-testid={testid}>
      <SEO
        title={finalData.title}
        description={(finalData.intro || "").slice(0, 155)}
        path={path}
        lang={lang}
        hreflangs={{ it: path, en: path, fr: path, de: path, es: path }}
      />
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{t("footer_legal")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-2 tracking-tight">
            {finalData.title}
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">{finalData.intro}</p>
          <div className="mt-4 font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">
            {t("policy_updated")}: {dateStr}
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-6">
        {finalData.sections.map((sec, i) => (
          <section
            key={i}
            data-testid={`${testid}-section-${i}`}
            className="bg-white border border-xd-border rounded-xl p-6 sm:p-7"
          >
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-xd-text">{sec.h}</h2>
            <p className="mt-3 text-sm sm:text-base text-xd-muted leading-relaxed whitespace-pre-line">
              {sec.p}
            </p>
          </section>
        ))}
      </article>

      <Footer />
    </div>
  );
}
